import moment from 'moment';

// IMP-447 — AdminServiceList.js / AdminProductList.js 의 중복 callbackDs(filter -> $match) 로직을
// 단일 공용 모듈로 이전. 기존 STRING/FIXLIST/NUMBER/LIKESTRING/DATETIME/BOOLEAN 분기는 그대로
// 보존하고, LIKESTRING 콤마 OR + 정규식 이스케이프 + 빈 key/빈 토큰 가드(§7 silent-drop 금지)만 추가한다.

const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

const escapeRegExp = (str) => String(str).replace(REGEX_SPECIAL_CHARS, '\\$&');

// F-001 (리뷰 대응): 큰따옴표로 감싼 구간은 콤마로 분해하지 않고 통째로 1토큰으로 취급한다.
// 따옴표는 오직 "토큰 시작 위치"(문자열 맨 앞 또는 콤마 직후, 공백 무시)에 있을 때만
// "감싸는 형태"로 인식한다. 값 중간의 따옴표(a"b)는 일반 리터럴 문자로 남는다.
// 닫는 따옴표가 없으면 따옴표 문자를 제거하고 나머지를 일반 콤마 분해 규칙으로 처리한다.
const parseQuoteAwareTokens = (val) => {
    const str = String(val ?? '');
    const n = str.length;
    const tokens = [];
    let i = 0;

    while (i < n) {
        let j = i;
        while (j < n && /\s/.test(str[j])) j++;

        if (str[j] === '"') {
            const closeIdx = str.indexOf('"', j + 1);

            if (closeIdx === -1) {
                // 닫는 따옴표 없음 — 따옴표 문자를 제거한 뒤 나머지는 일반 콤마 분해로 처리.
                str.slice(i).replace(/"/g, '').split(',').forEach((t) => tokens.push(t.trim()));
                i = n;
                break;
            }

            tokens.push(str.slice(j + 1, closeIdx));

            let k = closeIdx + 1;
            while (k < n && /\s/.test(str[k])) k++;
            i = (k < n && str[k] === ',') ? k + 1 : k;
        } else {
            const commaIdx = str.indexOf(',', j);
            if (commaIdx === -1) {
                tokens.push(str.slice(i).trim());
                i = n;
            } else {
                tokens.push(str.slice(i, commaIdx).trim());
                i = commaIdx + 1;
            }
        }
    }

    return tokens;
};

const splitTokens = (val) =>
    parseQuoteAwareTokens(val)
        .filter((token) => token.length > 0)
        .map((token) => escapeRegExp(token));

/**
 * DeepSearch filter 배열을 몽고 $match 객체로 변환한다.
 * @param {{key: string, val: any, type: string}[]} filters
 * @returns {Object} match
 */
export const buildDeepSearchMatch = (filters) => {
    const mt = {};

    (Array.isArray(filters) ? filters : []).forEach((x) => {
        const key = x?.key;

        if (typeof key !== 'string' || key.trim() === '') {
            console.warn('[guard] deepSearchMatch filter dropped — empty key', {
                handler: 'buildDeepSearchMatch',
                key,
                reason: 'filter.key is empty; dropping filter to avoid "" key in $match',
            });
            return;
        }

        if (x.type === 'STRING' || x.type === 'FIXLIST' || x.type === 'NUMBER') {
            mt[key] = x.val;
        }

        if (x.type === 'LIKESTRING') {
            const existing = mt[key];
            const existingTokens = existing && existing.$regex ? String(existing.$regex).split('|') : [];
            const nextTokens = splitTokens(x.val);
            const merged = [...existingTokens, ...nextTokens];

            if (merged.length === 0) {
                console.warn('[guard] deepSearchMatch LIKESTRING filter dropped — empty tokens', {
                    handler: 'buildDeepSearchMatch',
                    key,
                    reason: 'all comma-separated tokens are empty; dropping filter',
                });
            } else {
                mt[key] = { $regex: merged.join('|'), $options: 'si' };
            }
        }

        if (x.type === 'DATETIME') {
            const current = mt[key] && typeof mt[key] === 'object' ? mt[key] : {};
            mt[key] = current;
            if (x.val.opt === 'last') {
                mt[key]['$gte'] = moment().add(-1 * x.val.lastVal, x.val.lastValOpt).valueOf();
            } else {
                mt[key]['$gte'] = x.val.s;
                mt[key]['$lte'] = x.val.e;
            }
        }

        if (x.type === 'BOOLEAN') {
            mt[key] = x.val;
        }
    });

    return mt;
};

export default buildDeepSearchMatch;
